import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryActionTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {
    countCustomNotificationList,
    createOrUpdateNotificationSetting,
    getPageCustomNotifications,
    getPageNotifications,
    readOrDiscard,
} from '../NotificationEndPoint'
import {showNotification} from '../NotificationService'

export const usePageNotifications = (
    requestorId,
    hospitalId,
    type,
    userRole,
    timezoneId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    return useQuery({
        queryKey: notificationKeys.page({
            hospitalId,
            type,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getPageNotifications(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        enabled:
            !!requestorId &&
            (userRole === SystemRole.ADMIN ||
                (userRole === SystemRole.USER_WITH_ROLE && !!hospitalId)) &&
            !!enabled,
    })
}

export const useCustomPageNotifications = (
    requestorId,
    hospitalId,
    type,
    userRole,
    timezoneId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    return useQuery({
        queryKey: notificationKeys.page({
            hospitalId,
            type,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getPageCustomNotifications(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        enabled:
            !!requestorId &&
            (userRole === SystemRole.ADMIN ||
                (userRole === SystemRole.USER_WITH_ROLE && !!hospitalId)) &&
            !!enabled,
    })
}

export const useCustomPageNotificationsFetchMore = requestorId => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            hospitalId,
            type,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
            actionType,
        }) =>
            getPageCustomNotifications(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            const {
                hospitalId,
                type,
                timezoneId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
                actionType,
            } = variables

            if (data?.length > 0) {
                // PageNumber = 0 means it's referencing to the original page
                queryClient.setQueryData(
                    notificationKeys.page({
                        hospitalId,
                        type,
                        timezoneId,
                        pageNumber: 0,
                        pageRows,
                        sortField,
                        sortOrder,
                        filterRequest,
                    }),
                    oldData => {
                        if (actionType === QueryActionTypes.replace) {
                            return data
                        }

                        return [...oldData, ...data]
                    },
                )
            }
        },
    })
}

export const useCountCustomNotificationList = (
    hospitalId,
    timezoneId,
    requestorId,
    filterRequest,
    enabled = true,
) => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    return useQuery({
        queryKey: notificationKeys.customCount({
            hospitalId,
            timezoneId,
            filterRequest,
        }),
        queryFn: () =>
            countCustomNotificationList(
                requestorId,
                !!hospitalId ? {...filterRequest, hospitalId} : filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
    })
}

export const usePageNotificationsFetchMore = requestorId => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            hospitalId,
            type,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
            actionType,
        }) =>
            getPageNotifications(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            const {
                hospitalId,
                type,
                timezoneId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
                actionType,
            } = variables

            if (data?.content?.length > 0) {
                // PageNumber = 0 means it's referencing to the original page
                queryClient.setQueryData(
                    notificationKeys.page({
                        hospitalId,
                        type,
                        timezoneId,
                        pageNumber: 0,
                        pageRows,
                        sortField,
                        sortOrder,
                        filterRequest,
                    }),
                    oldData => {
                        if (actionType === QueryActionTypes.replace) {
                            return data
                        }

                        return {
                            ...oldData,
                            ...data,
                            content: [...oldData.content, ...data.content],
                        }
                    },
                )
            }
        },
    })
}

export const useReadOrDiscardNotification = requestorId => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, status, notificationQueryKeys}) =>
            readOrDiscard(requestorId, id, status),
        onSuccess: (data, variables) => {
            const {id, status, notificationQueryKeys} = variables

            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data && !!data?.id) {
                if (status === 'read') {
                    queryClient.setQueryData(
                        notificationKeys.page({
                            ...notificationQueryKeys,
                        }),
                        oldData => {
                            const newContent = oldData.content.map(item => {
                                if (item.id === id) {
                                    return {
                                        ...item,
                                        status: 'read',
                                    }
                                }

                                return item
                            })

                            return {
                                ...oldData,
                                content: newContent,
                            }
                        },
                    )
                }

                if (status === 'discarded') {
                    queryClient.setQueryData(
                        notificationKeys.page({
                            ...notificationQueryKeys,
                        }),
                        oldData => {
                            const newContent = oldData.content.filter(
                                item => item.id !== id,
                            )

                            return {
                                ...oldData,
                                totalElements:
                                    oldData.totalElements - 1 > 0
                                        ? oldData.totalElements - 1
                                        : 0,
                                content: newContent,
                            }
                        },
                    )
                }
            }
        },
    })
}

export const useNotificationSettingCRUD = (
    requestorId,
    callback = () => {},
) => {
    const {notificationKeys} = QueryKeyManager(
        QueryKeyTypes.notification,
        requestorId,
    )

    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data =>
            createOrUpdateNotificationSetting(requestorId, data),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data && (data === HTTP_STATUS_OK || data?.id)) {
                if (callback) {
                    callback({
                        key: 'notificationSetting',
                        data: data?.id ? {...data} : {...variables},
                    })
                }

                showNotification(
                    'success',
                    'Notification setting saved successfully',
                )
            }
        },
    })
}
